import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const EMPTY = {
  title: "",
  description: "",
  href: "",
  format: "PDF",
  category: "",
  order: 0,
};

export default function AdminResourceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const { data: cats } = await api.get("/resource-categories");
        setCategories(cats);
        if (!isEditing) {
          setForm((f) => ({ ...f, category: cats[0]?.slug || "" }));
        } else {
          const { data } = await api.get(`/admin/resources/${id}`);
          setForm({
            title: data.title || "",
            description: data.description || "",
            href: data.href || "",
            format: data.format || "PDF",
            category: data.category || cats[0]?.slug || "",
            order: data.order ?? 0,
          });
        }
      } catch {
        toast.error("Failed to load");
        navigate("/admin/resources");
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [id, isEditing, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (isEditing) {
        await api.put(`/admin/resources/${id}`, payload);
        toast.success("Resource updated");
      } else {
        await api.post("/admin/resources", payload);
        toast.success("Resource created");
      }
      navigate("/admin/resources");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save resource");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-ink-soft" data-testid="resource-editor-loading">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-resource-editor">
      <header className="border-b border-line bg-bone-surface">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin/resources" className="flex items-center gap-2 text-ink hover:text-forest text-sm" data-testid="resource-editor-back">
            <ArrowLeft className="w-4 h-4" /> Back to resources
          </Link>
          <span className="text-xs uppercase tracking-[0.2em] text-gold-deep">{isEditing ? "Edit Resource" : "New Resource"}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-serif text-4xl text-forest tracking-tight mb-8">{isEditing ? "Edit resource" : "Add a resource"}</h1>

        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={form.title}
              required
              onChange={(e) => update("title", e.target.value)}
              className="mt-2 bg-bone border-line"
              data-testid="resource-editor-title"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="mt-2 bg-bone border-line min-h-[110px]"
              data-testid="resource-editor-description"
            />
          </div>

          <div>
            <Label htmlFor="href">File URL <span className="text-ink-soft text-xs">(PDF link — paste after uploading)</span></Label>
            <Input
              id="href"
              type="url"
              required
              value={form.href}
              onChange={(e) => update("href", e.target.value)}
              placeholder="https://…"
              className="mt-2 bg-bone border-line font-mono text-sm"
              data-testid="resource-editor-href"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <Label htmlFor="category">Category</Label>
              <select
                id="category"
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                required
                className="mt-2 w-full bg-bone border border-line px-3 py-2 text-sm h-10 rounded-none"
                data-testid="resource-editor-category"
              >
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="format">Format</Label>
              <Input
                id="format"
                value={form.format}
                onChange={(e) => update("format", e.target.value)}
                className="mt-2 bg-bone border-line"
                data-testid="resource-editor-format"
              />
            </div>
            <div>
              <Label htmlFor="order">Order <span className="text-ink-soft text-xs">(lower first)</span></Label>
              <Input
                id="order"
                type="number"
                value={form.order}
                onChange={(e) => update("order", e.target.value)}
                className="mt-2 bg-bone border-line"
                data-testid="resource-editor-order"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="bg-forest text-bone hover:bg-forest-light rounded-none px-7 py-5"
              data-testid="resource-editor-save"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : isEditing ? "Save changes" : "Create resource"}
            </Button>
            <Link to="/admin/resources" className="text-ink-soft hover:text-forest text-sm" data-testid="resource-editor-cancel">Cancel</Link>
          </div>
        </form>
      </main>
    </div>
  );
}
